import { useState } from 'react';
import { Button, Pagination as BronsonPagination, Table } from '@vwfs-bronson/bronson-react';
import { Job, JOBS_PAGE_SIZE, Pagination } from '../../fes';
import { openDatadogLogs } from './datadog';

const INITIAL_PROMPT_MAX_LENGTH = 80;
const MAX_PAGE_BUTTONS = 5;

function truncate(value: string | undefined, maxLength: number): string {
  if (!value) {
    return '-';
  }

  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

function getPageWindow(currentPage: number, totalPages: number, maxButtons: number): number[] {
  const span = Math.min(maxButtons, totalPages);
  let start = currentPage - Math.floor(span / 2);
  start = Math.max(1, Math.min(start, totalPages - span + 1));

  return Array.from({ length: span }, (_, index) => start + index);
}

type JobsTableProps = {
  jobs: Job[];
  pagination?: Pagination;
  selectedJobId?: string;
  onSelectJob: (job: Job) => void;
  onPageChange: (offset: number) => Promise<void>;
};

export function JobsTable({ jobs, pagination, selectedJobId, onSelectJob, onPageChange }: JobsTableProps) {
  const [isPaginating, setIsPaginating] = useState(false);

  const limit = pagination?.limit ?? JOBS_PAGE_SIZE;
  const offset = pagination?.offset ?? 0;
  const total = pagination?.total ?? jobs.length;
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  const currentPage = limit > 0 ? Math.floor(offset / limit) + 1 : 1;

  const goToPage = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || isPaginating) {
      return;
    }

    setIsPaginating(true);
    try {
      await onPageChange((page - 1) * limit);
    } finally {
      setIsPaginating(false);
    }
  };

  if (jobs.length === 0) {
    return <p className="app-empty">No jobs found.</p>;
  }

  return (
    <div className="app-table-wrap">
      <Table className="app-table" colored>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Request ID</Table.Th>
            <Table.Th>Installation ID</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Created At</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Initial Prompt</Table.Th>
            <Table.Th>Logs</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map(job => (
            <Table.Tr
              key={job.requestId}
              aria-selected={job.requestId === selectedJobId}
              className="app-table-row--interactive"
              onClick={() => onSelectJob(job)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectJob(job);
                }
              }}
              selected={job.requestId === selectedJobId}
              tabIndex={0}
            >
              <Table.Td>{job.requestId}</Table.Td>
              <Table.Td>{job.installationId ?? '-'}</Table.Td>
              <Table.Td>{job.status || '-'}</Table.Td>
              <Table.Td>{job.createdAt || '-'}</Table.Td>
              <Table.Td>{job.email || '-'}</Table.Td>
              <Table.Td title={job.initialPrompt}>{truncate(job.initialPrompt, INITIAL_PROMPT_MAX_LENGTH)}</Table.Td>
              <Table.Td>
                <Button
                  ariaLabel={`Open Datadog logs for job ${job.requestId}`}
                  className="app-table-icon-button"
                  hiddenLabel
                  icon="semantic-forward"
                  incognito
                  onClick={event => {
                    event.stopPropagation();
                    openDatadogLogs(job.requestId);
                  }}
                  small
                  title="Open Datadog logs in a new tab"
                >
                  Datadog logs
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {totalPages > 1 ? (
        <BronsonPagination
          title="Jobs pagination"
          prevDisabled={isPaginating || currentPage <= 1}
          nextDisabled={isPaginating || currentPage >= totalPages}
          onPrevClick={() => goToPage(currentPage - 1)}
          onNextClick={() => goToPage(currentPage + 1)}
        >
          {getPageWindow(currentPage, totalPages, MAX_PAGE_BUTTONS).map(page => (
            <BronsonPagination.Item
              key={page}
              active={page === currentPage}
              disabled={isPaginating}
              onClick={() => goToPage(page)}
            >
              {page}
            </BronsonPagination.Item>
          ))}
        </BronsonPagination>
      ) : null}
    </div>
  );
}
